import { supabase } from '@/lib/supabase';

/**
 * Rider API - Get orders assigned to rider
 */

export interface RiderOrder {
  id: string;
  order_number: string;
  status: string;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  delivery_notes?: string;
  branch_id: string;
  rider_id?: string;
  total_amount: number;
  created_at: string;
  updated_at: string;
}

export interface GetRiderOrdersResponse {
  success: boolean;
  data: RiderOrder[];
}

/**
 * Fetch orders assigned to a rider
 */
export async function getRiderOrders(riderId: string, status?: string): Promise<GetRiderOrdersResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('rider-orders', {
      body: { rider_id: riderId, status },
    });

    if (error || !data?.success) {
      throw new Error(error?.message || data?.error || 'Failed to fetch orders');
    }

    return data;
  } catch (error) {
    console.error('[RiderAPI] Error fetching orders:', error);
    return {
      success: false,
      data: [],
    };
  }
}

/**
 * Confirm rider for an order (digital confirmation)
 */
export async function confirmRider(orderId: string, riderId: string): Promise<{ success: boolean; message?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('rider-orders', {
      body: { action: 'confirm_rider', order_id: orderId, rider_id: riderId, confirmation_method: 'DIGITAL' },
    });

    if (error) {
      throw new Error(error.message || 'Failed to confirm');
    }

    return data;
  } catch (error) {
    console.error('[RiderAPI] Error confirming rider:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to confirm',
    };
  }
}

/**
 * Start delivery (mark as picked up / out for delivery)
 */
export async function startDelivery(orderId: string, riderId: string): Promise<{ success: boolean; message?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('rider-orders', {
      body: { action: 'start_delivery', order_id: orderId, rider_id: riderId },
    });

    if (error) {
      throw new Error(error.message || 'Failed to start delivery');
    }

    return data;
  } catch (error) {
    console.error('[RiderAPI] Error starting delivery:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to start delivery',
    };
  }
}

/**
 * Complete delivery (mark as delivered)
 */
export async function completeDelivery(orderId: string, riderId: string): Promise<{ success: boolean; message?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('rider-orders', {
      body: { action: 'complete_delivery', order_id: orderId, rider_id: riderId },
    });

    if (error) {
      throw new Error(error.message || 'Failed to complete delivery');
    }

    return data;
  } catch (error) {
    console.error('[RiderAPI] Error completing delivery:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to complete delivery',
    };
  }
}
